import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAPI } from 'api';

import PageSection from 'components/PageSection';
import PropEditor from 'components/PropEditor';
import RawJsonDisplay from 'components/RawJsonDisplay';
import { OverrideRuleData, Category, UserGroup } from 'types';
import { OrgContextLink, useOrgContext } from 'components/OrgContextProvider';

interface OverrideRuleProps {
	onChanged: () => void;
}

const OverrideRule = ({ onChanged }: OverrideRuleProps) => {
	const api = useAPI();
	const navigate = useNavigate();
	const ctx = useOrgContext();

	const { ruleID } = useParams();

	const dateToLocalString = (date: Date) => {
		const d = new Date(date);
		d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
		return d.toISOString().slice(0, 16);
	};

	const [rule, setRule] = useState<OverrideRuleData>({
		name: '',
		allowedCategories: [],
		userGroups: [],
		start: dateToLocalString(new Date()),
		end: dateToLocalString(new Date(Number(new Date()) + (30 * 60000))),
		scheduleType: 'next30'
	});

	const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
	const [availableUserGroups, setAvailableUserGroups] = useState<UserGroup[]>([]);

	useEffect(() => {
		if (ruleID !== 'new') {
			api.get<OverrideRuleData>(`${ctx?.apiBaseWorkspace}/overrides/rules/${ruleID}`)
			.then(json => setRule({ ...json, scheduleType: 'custom' }));
		}

		api.get<Category[]>(`${ctx?.apiBaseWorkspace}/overrides/firewall/categories`)
		.then(json => setAvailableCategories(json));

		api.get<UserGroup[]>(`${ctx?.apiBaseWorkspace}/overrides/firewall/usergroups`)
		.then(json => setAvailableUserGroups(json));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const saveOverrideRule = () => {
		const saveRule: Partial<OverrideRuleData> & { start: string | Date; end: string | Date } = { ...rule };

		if (saveRule.scheduleType?.startsWith('next')) {
			const minutes = parseInt(saveRule.scheduleType.slice(4));
			saveRule.start = new Date();
			saveRule.end = new Date(Number(new Date()) + (minutes * 60000));
		}

		delete saveRule.scheduleType;

		if (ruleID === 'new') {
			api.post(`${ctx?.apiBaseWorkspace}/overrides/rules`, saveRule)
			.then(onChanged)
			.then(() => navigate(`${ctx?.contextPath}/overrides`));
		} else {
			api.put(`${ctx?.apiBaseWorkspace}/overrides/rules/${ruleID}`, saveRule)
			.then(onChanged)
			.then(() => navigate(`${ctx?.contextPath}/overrides`));
		}
	};

	return (
		<div>
			<PageSection borderEnd={true}>
				<OrgContextLink to={`/overrides`}>&#x25c0; Back to Overrides</OrgContextLink>
			</PageSection>

			<PageSection title={ruleID === 'new' ? 'New Override Rule' : `Override Rule '${rule?.name ?? ruleID}'`}>
				<PropEditor<OverrideRuleData>
					object={rule}
					props={[
						{ label: 'ID', key: 'id', disabled: true },
						{ label: 'Name', key: 'name' },
						{
							label: 'Allowed Categories',
							key: 'allowedCategories',
							type: 'multiselect' as const,
							options: availableCategories
								.filter(c => c.availability === 'available')
								.map(c => ({ label: c.name, value: c.id }))
						},
						{
							label: 'User Groups',
							key: 'userGroups',
							type: 'multiselect' as const,
							options: availableUserGroups
								.filter(g => g.availability === 'available')
								.map(g => ({ label: g.name, value: g.id }))
						},
						{ label: 'Schedule', key: 'scheduleType', type: 'select' as const, options: [
							{ label: 'Next 15 Minutes', value: 'next15' },
							{ label: 'Next 30 Minutes', value: 'next30' },
							{ label: 'Next 45 Minutes', value: 'next45' },
							{ label: 'Next 1 Hour', value: 'next60' },
							{ label: 'Custom', value: 'custom' }
						]},
						...(rule.scheduleType === 'custom' ? [
							{ label: 'Start', key: 'start', type: 'datetime' as const },
							{ label: 'End', key: 'end', type: 'datetime' as const }
						] : [])
					]}
					onChange={setRule}
					tools={[
						{ label: 'Save Override Rule', action: saveOverrideRule }
					]}
				/>
			</PageSection>

			<PageSection title="Raw Data">
				<RawJsonDisplay data={rule} />
			</PageSection>
		</div>
	);
};

export default OverrideRule;
